import { simpleCampaignsQueryOptions } from "@/api/campaign.api";
import { createDeliverySetupSteps } from "@/components/campaign/setup/delivery/steps";
import { createPlanningSetupSteps } from "@/components/campaign/setup/planning/steps";
import { AlertModal } from "@/components/generic/AlertModal";
import { Button } from "@/components/generic/Button";
import { MainWindow } from "@/components/layout/MainWindow";
import { PageHeader } from "@/components/layout/PageHeader";
import { Sidebar } from "@/components/layout/Sidebar";
import {
  HorizontalStepperSteps,
  StepperBottomBar,
  VerticalStepperSteps,
} from "@/components/StepperSteps";
import { TrialCTAModal } from "@/components/trial/TrialCTAModal";
import { useDedupedEffect } from "@/hooks/use-deduped-effect";
import { trialCTAModalPropsAtom } from "@/hooks/use-trial-period";
import { getConfigurationSuggestions } from "@/queries/configurations";
import { trackEvent } from "@/services/amplitude.service";
import { newCampaignStateAtom } from "@/services/new-campaign.service";
import { sessionId } from "@/services/session-id.service";
import { Configuration } from "@/types/campaign";
import { assertDefined } from "@/utils/common";
import { store } from "@/utils/jotai";
import { createStepper } from "@/utils/stepper";
import { queryOptions, useQuery } from "@tanstack/react-query";
import {
  createFileRoute,
  Outlet,
  redirect,
  useBlocker,
  useMatches,
} from "@tanstack/react-router";
import { AnimatePresence, motion } from "framer-motion";
import { getDefaultStore, useAtom } from "jotai";
import { RESET } from "jotai/utils";
import { useMemo, useRef } from "react";
import { twMerge } from "tailwind-merge";
import z from "zod";

const stepperSymbol = Symbol();
const configurationSuggestionsQuerySymbol = Symbol();

export const Route = createFileRoute("/_dashboard/campaigns/setup")({
  component: RouteComponent,
  beforeLoad: async ({ matches, context }) => {
    const jotaiStore = getDefaultStore();
    const { queryClient, trialPeriod } = context;
    const isTrialPeriod = !!trialPeriod;
    const { appConfiguration } = context;

    if (trialPeriod) {
      const simpleCampaigns = await queryClient.ensureQueryData(
        simpleCampaignsQueryOptions,
      );

      if (trialPeriod.isExpired || simpleCampaigns.length >= 3)
        throw redirect({
          from: Route.fullPath,
          to: "/campaigns",
        });
    }

    jotaiStore.set(newCampaignStateAtom, (prev) => {
      let planning = prev.planning;
      let delivery = prev.delivery;

      if (isTrialPeriod) {
        if (planning) {
          if (planning.type !== "OnDemand")
            planning = {
              type: "OnDemand",
              leadAmount: 10,
              deliverContacts: planning.deliverContacts,
              deliverContactsRequirePhone: planning.deliverContactsRequirePhone,
              deliverLiveNumber: planning.deliverLiveNumber,
            };

          if (planning.leadAmount !== 10) planning.leadAmount = 10;
        }

        if (delivery?.type !== "LassoSales")
          delivery = {
            type: "LassoSales",
          };
      }

      if (
        prev.draftId &&
        prev.sessionId &&
        prev.isTrialPeriod === isTrialPeriod &&
        prev.canSendContactsToIntegration ===
          appConfiguration.features.canSendContactsToIntegration &&
        prev.canSendLiveNumbersToIntegration ===
          appConfiguration.features.canSendLiveNumbersToIntegration &&
        prev.responsibleId &&
        prev.planning === planning &&
        prev.delivery === delivery
      )
        return prev;

      let draftId = prev.draftId;
      if (!draftId) {
        draftId = crypto.randomUUID();
        trackEvent("Campaign creation started", context.user.id, {
          draftId: draftId,
        });
      }

      return {
        ...prev,
        draftId,
        sessionId: prev.sessionId || sessionId,
        isTrialPeriod,
        canSendContactsToIntegration:
          appConfiguration.features.canSendContactsToIntegration,
        canSendLiveNumbersToIntegration:
          appConfiguration.features.canSendLiveNumbersToIntegration,
        responsibleId: prev.responsibleId ?? context.user.id,
        planning,
        delivery,
      };
    });

    const stepper = createStepper({
      symbol: stepperSymbol,
      context: jotaiStore.get(newCampaignStateAtom),
      steps: [
        {
          id: "define",
          name: "Målgruppe",
          description: "Definer din målgruppe og kriterier",
          to: "/campaigns/setup/define",
        },
        {
          id: "delivery",
          name: "Levering",
          description: "Vælg hvor du ønsker at modtage leads",
          to: "/campaigns/setup/delivery",
          getIsLocked: (context) => context.isTrialPeriod,
          steps: createDeliverySetupSteps({
            method: {
              id: "delivery/method",
              to: "/campaigns/setup/delivery/method",
            },
            configure: {
              id: "delivery/configure",
              to: "/campaigns/setup/delivery/configure",
            },
            "integration/select": {
              id: "delivery/integration/select",
              to: "/campaigns/setup/delivery/integration/select",
            },
            "integration/method": {
              id: "delivery/integration/method",
              to: "/campaigns/setup/delivery/integration/method",
            },
            "integration/configure": {
              id: "delivery/integration/configure",
              to: "/campaigns/setup/delivery/integration/configure",
            },
          }),
        },
        {
          id: "planning",
          name: "Planlægning",
          description: "Tilpas hvornår leads skal leveres",
          to: "/campaigns/setup/planning",
          steps: createPlanningSetupSteps({
            type: {
              id: "planning/type",
              to: "/campaigns/setup/planning/type",
            },
            "lead-amount": {
              id: "planning/lead-amount",
              to: "/campaigns/setup/planning/lead-amount",
            },
            triggers: {
              id: "planning/triggers",
              to: "/campaigns/setup/planning/triggers",
            },
            "lead-amount-and-period": {
              id: "planning/lead-amount-and-period",
              to: "/campaigns/setup/planning/lead-amount-and-period",
            },
            "sales-strategy-and-prioritization": {
              id: "planning/sales-strategy-and-prioritization",
              to: "/campaigns/setup/planning/sales-strategy-and-prioritization",
            },
            "contact-information": {
              id: "planning/contact-information",
              to: "/campaigns/setup/planning/contact-information",
            },
          }),
        },
        {
          id: "details",
          name: "Detaljer",
          description: "Vælg kampagnenavn og ansvarlig",
          to: "/campaigns/setup/details",
        },
        {
          id: "summary",
          name: "Opsummering",
          description: "Bekræft og opret din kampagne",
          to: "/campaigns/setup/summary",
        },
      ],
      treatLockedAsNotVisible: true,

      // If this draft is from a different session, we can no longer rely on the completed step ids, as we might've changed the steps since then.
      requireCompleteToAccessNextStep:
        jotaiStore.get(newCampaignStateAtom).sessionId === sessionId,
      getCompletedStepIds: (context) => context.completedStepIds,
      setCompletedStepIds: (newCompletedStepIds, setContext) => {
        setContext((context) => ({
          ...context,
          completedStepIds: newCompletedStepIds,
        }));
      },

      getLastVisitedStepMap: (context) => context.lastVisitedStepMap,
      setLastVisitedStepMap: (newLastVisitedStepMap) => {
        jotaiStore.set(newCampaignStateAtom, (prev) => ({
          ...prev,
          lastVisitedStepMap: newLastVisitedStepMap,
        }));
      },
    });

    stepper.onBeforeLoadStepRoute(Route.fullPath, matches);

    const useNewCampaignState = () => useAtom(newCampaignStateAtom);

    const useTrialCTAModal = () => useAtom(trialCTAModalPropsAtom);

    const createAppliedConfigurationSuggestionsQueryOptions = (
      mainConfiguration: Configuration,
    ) => {
      const configurationSuggestionsQueryOptions = getConfigurationSuggestions({
        configuration: mainConfiguration,
      });

      return {
        configurationSuggestionsQueryOptions,
        appliedConfigurationSuggestionsQueryOptions: queryOptions({
          queryKey: [configurationSuggestionsQuerySymbol, mainConfiguration],
          queryFn: async ({ client, signal }) => {
            const suggestions = await client.ensureQueryData(
              getConfigurationSuggestions({
                configuration: mainConfiguration,
              }),
            );

            signal.throwIfAborted();

            const newCampaignState = store.get(newCampaignStateAtom);

            const existingConfigurationsByQuality = new Map(
              newCampaignState.configurations.map((configuration) => [
                configuration.quality,
                configuration,
              ]),
            );

            const applySuggestion = (
              existingConfiguration: Configuration | undefined,
              suggestedConfiguration: Configuration,
            ) => ({
              ...suggestedConfiguration,
              priority:
                existingConfiguration?.priority ??
                suggestedConfiguration.priority,
              subPriority:
                existingConfiguration?.subPriority ??
                suggestedConfiguration.subPriority,
              idealLeadShare:
                existingConfiguration?.idealLeadShare ??
                suggestedConfiguration.idealLeadShare,
            });

            const updatedConfigurations = suggestions.map((s) => {
              const existingConfiguration = existingConfigurationsByQuality.get(
                s.configuration.quality,
              );

              if (existingConfiguration) {
                if (existingConfiguration.quality === 1) {
                  return {
                    ...applySuggestion(existingConfiguration, s.configuration),
                    enabled: true,
                  };
                }

                if (existingConfiguration.enabled) {
                  return existingConfiguration;
                }
              }

              return {
                ...applySuggestion(existingConfiguration, s.configuration),
                enabled: false,
              };
            });

            const updatedMainConfiguration = updatedConfigurations.find(
              (c) => c.quality === 1,
            );

            // This is a sanity check to make sure that the main configuration hasn't changed since we last fetched the suggestions, as that would mean that the suggestions are no longer valid.
            if (
              newCampaignState.configurations.find(
                (c) => c.quality === mainConfiguration.quality,
              ) === mainConfiguration
            ) {
              if (updatedMainConfiguration) {
                client.setQueryData(
                  getConfigurationSuggestions({
                    configuration: updatedMainConfiguration,
                  }).queryKey,
                  suggestions,
                );
              }

              store.set(newCampaignStateAtom, {
                ...newCampaignState,
                configurations: updatedConfigurations,
              });
            }

            return updatedConfigurations;
          },
          refetchOnMount: false,
          refetchOnReconnect: false,
          refetchOnWindowFocus: false,
          staleTime: Infinity,
        }),
      };
    };

    const mainConfiguration = assertDefined(
      store
        .get(newCampaignStateAtom)
        .configurations.find((c) => c.quality === 1),
    );

    const {
      configurationSuggestionsQueryOptions,
      appliedConfigurationSuggestionsQueryOptions,
    } = createAppliedConfigurationSuggestionsQueryOptions(mainConfiguration);

    const useAppliedConfigurationSuggestions = () => {
      const [newCampaignState] = useNewCampaignState();

      const mainConfiguration = useMemo(
        () =>
          assertDefined(
            newCampaignState.configurations.find((c) => c.quality === 1),
          ),
        [newCampaignState.configurations],
      );

      const {
        configurationSuggestionsQueryOptions,
        appliedConfigurationSuggestionsQueryOptions,
      } = useMemo(
        () =>
          createAppliedConfigurationSuggestionsQueryOptions(mainConfiguration),
        [mainConfiguration],
      );

      const configurationSuggestionsQuery = useQuery(
        configurationSuggestionsQueryOptions,
      );

      const appliedConfigurationSuggestionsQuery = useQuery(
        appliedConfigurationSuggestionsQueryOptions,
      );

      return {
        configurationSuggestionsQuery,
        appliedConfigurationSuggestionsQuery,
      };
    };

    return {
      configurationSuggestionsQueryOptions,
      appliedConfigurationSuggestionsQueryOptions,
      useAppliedConfigurationSuggestions,
      stepper,
      useStepper: () => {
        const [campaignState, setCampaignState] = useNewCampaignState();

        return stepper.useStepper(
          useMemo(
            () => ({
              context: campaignState,
              setContext: setCampaignState,
            }),
            [campaignState, setCampaignState],
          ),
        );
      },
      newCampaignStateAtom,
      useNewCampaignState,
      useTrialCTAModal,
    };
  },
  loader: ({ context, route }) => {
    const title = context.stepper.stepperState.flatStepsByTo.get(
      route.fullPath,
    )?.name;
    return {
      meta: { title },
    };
  },
});

function RouteComponent() {
  const context = Route.useRouteContext();
  const navigate = Route.useNavigate();

  const [newCampaignState, setNewCampaignState] = context.useNewCampaignState();

  context.useAppliedConfigurationSuggestions();

  const stepper = context.useStepper();

  const [trialCTAModalProps, setTrialCTAModalProps] =
    context.useTrialCTAModal();

  const matches = useMatches();

  const activeRootStep = useMemo(() => {
    const rootStepsMap = new Map(
      stepper.visibleSteps.map((s) => [String(s.id), s] as const),
    );
    for (const step of stepper.activeFlatSteps) {
      const rootStep = rootStepsMap.get(step.id);
      if (rootStep) return rootStep;
    }
    return null;
  }, [stepper.activeFlatSteps, stepper.visibleSteps]);

  const hideSubStepperSteps = useMemo(
    () => !activeRootStep || activeRootStep.visibleSteps.length <= 1,
    [activeRootStep],
  );

  const { showSubStepper, showBottomBar, hasPadding, hideSubmitButton } =
    useMemo(() => {
      let showSubStepper = true;
      let showBottomBar = true;
      let hasPadding = true;
      let hideSubmitButton = false;

      const schema = z
        .object({
          showCampaignSetupSubStepper: z.boolean().catch(true),
          showCampaignSetupBottomBar: z.boolean().catch(true),
          hasCampaignSetupPadding: z.boolean().catch(true),
          hideSubmitButton: z.boolean().catch(false),
        })
        .optional();

      for (const match of [...matches].reverse()) {
        for (const rawData of [match.loaderData, match.staticData]) {
          if (!showSubStepper && !showBottomBar) break;

          const data = schema.parse(rawData);
          if (!data) continue;

          if (!data.showCampaignSetupSubStepper) showSubStepper = false;
          if (!data.showCampaignSetupBottomBar) showBottomBar = false;
          if (!data.hasCampaignSetupPadding) hasPadding = false;
          if (data.hideSubmitButton) hideSubmitButton = true;
        }

        if (!showSubStepper && !showBottomBar) break;
      }

      return { showSubStepper, showBottomBar, hasPadding, hideSubmitButton };
    }, [matches]);

  const ignoreBlockerRef = useRef(false);

  const blocker = useBlocker({
    shouldBlockFn: ({ next }) => {
      if (ignoreBlockerRef.current) return false;
      if (next.fullPath.startsWith("/campaigns/setup")) return false;
      return true;
    },
    withResolver: true,
    enableBeforeUnload: false,
  });

  // Track opening/closing of the blocker AlertDialog
  useDedupedEffect((isInitial) => {
    // Don't track the initial idle state as closing the blocker.
    if (blocker.status === "idle" && isInitial) return;

    if (blocker.status === "blocked") {
      trackEvent(
        "Campaign creation -> save/discard draft modal shown",
        context.user.id,
        {
          draftId: newCampaignState.draftId,
        },
      );
    } else {
      trackEvent(
        "Campaign creation -> save/discard draft modal closed",
        context.user.id,
        {
          draftId: newCampaignState.draftId,
        },
      );
    }
  }, blocker.status);

  return (
    <>
      <Sidebar direction="right">
        <div className="flex size-full flex-col">
          <PageHeader
            className="px-8 xl:px-8"
            title="Ny kampagne"
            sticky={false}
          />

          <div className="flex flex-col p-8">
            <VerticalStepperSteps stepper={stepper} />
          </div>
        </div>
      </Sidebar>

      <MainWindow className="relative">
        <div className="flex h-full flex-col">
          <div
            className={twMerge(
              "flex grow flex-col justify-center gap-y-8 xl:gap-y-14",
              hasPadding ? "p-8 xl:p-14" : "px-0",
            )}
          >
            {showSubStepper && activeRootStep?.visibleSteps.length ? (
              <AnimatePresence key={activeRootStep.id} initial={false}>
                {!hideSubStepperSteps && (
                  <motion.div
                    className="flex items-center justify-center"
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                  >
                    <HorizontalStepperSteps
                      stepper={stepper}
                      steps={activeRootStep.visibleSteps}
                    />
                  </motion.div>
                )}
              </AnimatePresence>
            ) : null}

            <div className="flex grow flex-col justify-center">
              <Outlet />
            </div>
          </div>

          {showBottomBar && (
            <StepperBottomBar
              stepper={stepper}
              hideSubmitButton={hideSubmitButton}
              slots={{
                beforeSubmitButton: () =>
                  activeRootStep?.id === "define" ? (
                    <Button
                      intent="secondary"
                      onPress={() => {
                        trackEvent(
                          "Campaign creation -> save draft button clicked",
                          context.user.id,
                          {
                            draftId: newCampaignState.draftId,
                          },
                        );

                        ignoreBlockerRef.current = true;
                        navigate({
                          to: "/campaigns",
                        });
                      }}
                    >
                      Gem kladde
                    </Button>
                  ) : undefined,
              }}
            />
          )}
        </div>
      </MainWindow>

      <TrialCTAModal
        isOpen={!!trialCTAModalProps}
        onOpenChange={(open) =>
          setTrialCTAModalProps(open ? trialCTAModalProps : undefined)
        }
        title={trialCTAModalProps?.title ?? ""}
      />

      <AlertModal
        title="Din kampagne er ikke gemt"
        // description="Vil du gemme dine ændringer som en kladde, så du kan vende tilbage og færdiggøre din kampagne senere? Hvis ikke, vil dine ændringer gå tabt."
        description="Du kan gemme din kampagne som kladde og færdiggøre den senere. Hvis ikke vil dine ændringer gå tabt."
        isOpen={blocker.status === "blocked"}
        size="lg"
        cancelButtonText="Gem ikke"
        confirmButtonText="Gem som kladde"
        onClose={blocker.reset}
        onCancel={() => {
          trackEvent(
            "Campaign creation -> save/discard draft modal -> discard button clicked",
            context.user.id,
            {
              draftId: newCampaignState.draftId,
            },
          );

          trackEvent("Campaign creation discarded", context.user.id, {
            draftId: newCampaignState.draftId,
          });

          blocker.proceed?.();
          setNewCampaignState(RESET);
        }}
        onConfirm={() => {
          trackEvent(
            "Campaign creation -> save/discard draft modal -> save button clicked",
            context.user.id,
            {
              draftId: newCampaignState.draftId,
            },
          );

          blocker.proceed?.();
        }}
      />
    </>
  );
}
